import { baseType, isInertImageType } from '@/features/message-frame/subresources';

/*
 * # What an attachment preview may be, and what it may never be
 *
 * An attachment is bytes a stranger chose. The reading pane already treats
 * them that way — they are fetched by the page with a session proof (ADR
 * 0041), minted through `ObjectUrlRegistry`, which re-types anything that is
 * not a raster image to `application/octet-stream` because a `blob:` URL
 * inherits *this app's* origin and a markup document opened from one runs as
 * the signed-in user (ADR 0052, #267).
 *
 * A preview must not be the hole in that. So there are exactly two previews,
 * and each is contained by construction rather than by policy:
 *
 * - **image** — only the five raster types `ObjectUrlRegistry` keeps
 *   (`isInertImageType`, the single authority; this module keeps no second
 *   list). The bytes reach the screen as `<img src="blob:…">`, which is an
 *   image decode and nothing else: no scripting context, no navigation, and
 *   `img-src 'self' blob: data:` is already in the app-shell CSP (ADR 0049),
 *   so no policy has to be widened. If the registry ever stopped keeping a
 *   type, the blob would arrive as `application/octet-stream` and simply fail
 *   to decode — the failure mode is a broken image, never a live document.
 *
 * - **text** — an allow-list of types that are *text a reader wants to read*.
 *   The bytes are decoded here and rendered as a React text child of a
 *   `<pre>`, so they are escaped by the renderer and never parsed as markup.
 *   No object URL is minted for a text preview at all: nothing is ever
 *   navigable.
 *
 * Everything else gets **no preview** — one line saying so, plus the type,
 * the size and Download. Specifically:
 *
 * - **PDF**: there is no honest in-pane PDF preview here. The browser's own
 *   viewer needs a *document* context — `<iframe>`, `<embed>` or `<object>` —
 *   and all three are refused by the app-shell CSP (`frame-src 'none'`,
 *   `object-src 'none'`). Loosening that is not the hard part: the frame
 *   cannot point at `/api/…` (ADR 0041 removed cookie-only GETs), so it would
 *   have to point at a `blob:` URL, which means minting `application/pdf`
 *   instead of `application/octet-stream` — undoing exactly the re-typing
 *   that exists to stop sender bytes becoming a same-origin document. A
 *   same-origin PDF document is a viewer (with its own scripting support and
 *   its own history of escapes) running where this app's session and device
 *   key live. pdf.js would avoid the origin problem, but it is ~350 KB and
 *   docs/plan/06-frontend-v1.md §12 rules it out for v1. So: download only,
 *   and say so.
 *
 * - **HTML, XHTML, XML and SVG**: markup. Never previewed, in any wrapper.
 *   An SVG is not rasterized here either: the body-image path does that
 *   because it must show an image the sender put *in the message*, under a
 *   per-sender grant; an attachment row has no such need, and "the row did
 *   not draw it" is a containment argument with no moving parts.
 *
 * - **audio/video**: the app-shell CSP grants no `media-src`, so it falls back
 *   to `default-src 'none'`. A player would need that ADR amended; it is not
 *   worth a policy grant for a convenience, so these are download-only too.
 */

export type PreviewKind = 'image' | 'text' | 'none';

export interface PreviewPlan {
  kind: PreviewKind;
  /** Present exactly when `kind` is `'none'`: one line, shown in the row. */
  reason?: string;
}

/**
 * Text types worth putting on screen.
 *
 * This is a UX list, not a safety list — rendering *any* bytes as a React
 * text child is safe. It is an allow-list rather than "anything `text/*`"
 * because `text/html` is `text/*` too, and because a wall of decoded binary
 * is a worse answer than "no preview".
 */
const TEXT_PREVIEW_TYPES: ReadonlySet<string> = new Set([
  'text/plain',
  'text/csv',
  'text/tab-separated-values',
  'text/markdown',
  'text/x-markdown',
  'text/calendar',
  'text/vcard',
  'text/x-vcard',
  'text/css',
  'text/x-log',
  'text/javascript',
  'application/javascript',
  'application/x-javascript',
  'text/x-typescript',
  'application/json',
  'application/ld+json',
  'application/x-ndjson',
  'text/yaml',
  'text/x-yaml',
  'application/yaml',
  'application/x-yaml',
  'application/toml',
  'text/x-toml',
  'text/x-python',
  'text/x-go',
  'text/x-java-source',
  'text/x-c',
  'text/x-c++src',
  'text/x-csrc',
  'text/x-sql',
  'text/x-shellscript',
  'application/x-sh',
  'text/x-diff',
  'text/x-patch',
]);

/** Types whose "no preview" line should say what the type is, not just that it is. */
const MARKUP_TYPES: ReadonlySet<string> = new Set([
  'text/html',
  'application/xhtml+xml',
  'text/xml',
  'application/xml',
  'image/svg+xml',
]);

const PDF_REASON =
  'No preview — a PDF has to be opened as a document, and this app never turns a sender’s bytes into one. Download it to read it.';
const MARKUP_REASON =
  'No preview — this type is markup and can carry script, so it is only ever offered as a download.';
const DEFAULT_REASON = 'No preview for this type — download it to open it.';

/** What the pane may do with a part of this media type. */
export function planPreview(contentType: string): PreviewPlan {
  if (isInertImageType(contentType)) return { kind: 'image' };
  const type = baseType(contentType);
  if (TEXT_PREVIEW_TYPES.has(type)) return { kind: 'text' };
  if (type === 'application/pdf') return { kind: 'none', reason: PDF_REASON };
  if (MARKUP_TYPES.has(type)) return { kind: 'none', reason: MARKUP_REASON };
  return { kind: 'none', reason: DEFAULT_REASON };
}

/** The longest sender-controlled media type the row will draw. */
const MAX_TYPE_LABEL = 48;

/**
 * The media type as a row shows it.
 *
 * The string came from the sender's MIME headers, so it is bounded here:
 * React escapes it, but an unbounded one would still wreck the row.
 */
export function typeLabel(contentType: string): string {
  const type = baseType(contentType);
  if (type === '') return 'unknown type';
  return type.length > MAX_TYPE_LABEL ? `${type.slice(0, MAX_TYPE_LABEL - 1)}…` : type;
}

/** How much of a text part is decoded for the preview. The rest is a download. */
export const MAX_TEXT_PREVIEW_BYTES = 128 * 1024;

/**
 * Control characters that mean something to a terminal and nothing to a
 * reader. C0 minus tab/newline/carriage return, plus DEL and the C1 block.
 * Dropping them keeps the pane honest about what the file contains — an
 * `ESC[2J` in a log should not look like blank space.
 */
// eslint-disable-next-line no-control-regex -- control characters are the point.
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/g;

/** Decodes a text part for the preview, capped, with the control bytes dropped. */
export async function readPreviewText(blob: Blob): Promise<{ text: string; truncated: boolean }> {
  const truncated = blob.size > MAX_TEXT_PREVIEW_BYTES;
  const slice = truncated ? blob.slice(0, MAX_TEXT_PREVIEW_BYTES) : blob;
  const raw = await slice.text();
  return { text: raw.replace(CONTROL_CHARACTERS, ''), truncated };
}
